import type { Word, WordList } from "./types";
import { catalogWords } from "./catalog";

export const lists: WordList[] = [
  {
    id: "nuances",
    numeral: "I",
    title: { fr: "L’art de la nuance", en: "The art of nuance" },
    subtitle: { fr: "Précision & pensée", en: "Precision & thought" },
    description: {
      fr: "Des mots pour distinguer, préciser et donner à la pensée son juste contour.",
      en: "Words that distinguish, refine, and give thought its proper contour.",
    },
    accent: "#7b3038",
  },
  {
    id: "poetics",
    numeral: "II",
    title: { fr: "Poétique du monde", en: "Poetics of the world" },
    subtitle: { fr: "Beauté & sensation", en: "Beauty & sensation" },
    description: {
      fr: "Une collection de mots rares où la lumière, le temps et le paysage deviennent langage.",
      en: "A collection of rare words in which light, time, and landscape become language.",
    },
    accent: "#31564d",
  },
  {
    id: "inheritance",
    numeral: "III",
    title: { fr: "Héritages littéraires", en: "Literary inheritances" },
    subtitle: { fr: "Textes & mémoire", en: "Texts & memory" },
    description: {
      fr: "Des mots passés par les livres, les siècles et les conversations savantes.",
      en: "Words carried through books, centuries, and learned conversations.",
    },
    accent: "#8b6331",
  },
  {
    id: "essential_en", numeral: "EN", language: "en", defaultEnabled: true,
    title: { fr: "Anglais essentiel", en: "Essential English" },
    subtitle: { fr: "Précision & usage", en: "Precision & usage" },
    description: { fr: "Des mots anglais expressifs et réellement utiles à la lecture.", en: "Expressive English words that reward repeated use and reading." },
    accent: "#31564d",
  },
  {
    id: "essential_fr", numeral: "FR", language: "fr", defaultEnabled: true,
    title: { fr: "Français essentiel", en: "Essential French" },
    subtitle: { fr: "Nuance & justesse", en: "Nuance & precision" },
    description: { fr: "Un premier cercle de mots français précis, littéraires sans être inutiles.", en: "A first circle of precise French words: literary without being impractical." },
    accent: "#7a343b",
  },
  {
    id: "latin_phrases", numeral: "LA", language: "la", defaultEnabled: true,
    title: { fr: "Locutions latines", en: "Latin phrases" },
    subtitle: { fr: "Mémoire & maxime", en: "Memory & maxim" },
    description: { fr: "Des formules latines, leur traduction et leur sens véritable.", en: "Latin expressions with translations and their fuller meanings." },
    accent: "#8a6335",
  },
  {
    id: "english_literary", numeral: "I", language: "en",
    title: { fr: "Anglais littéraire", en: "Literary English" },
    subtitle: { fr: "Romans & poésie", en: "Novels & poetry" },
    description: { fr: "Des mots rencontrés dans les romans, poèmes et essais anglophones.", en: "Words encountered in English novels, poems, and essays." },
    accent: "#4c5268",
  },
  {
    id: "french_literary", numeral: "II", language: "fr",
    title: { fr: "Français retrouvé", en: "Rediscovered French" },
    subtitle: { fr: "Rare & savoureux", en: "Rare & vivid" },
    description: { fr: "Des mots français rares, anciens ou simplement trop peu employés.", en: "Rare, older, or simply underused French words." },
    accent: "#86504b",
  },
  {
    id: "mind_feeling", numeral: "III", language: "multi",
    title: { fr: "Sentiments & conscience", en: "Feeling & consciousness" },
    subtitle: { fr: "L’intime nommé", en: "Naming inner life" },
    description: { fr: "Nommer avec finesse émotions, états d’âme et mouvements intérieurs.", en: "Precise language for emotions, moods, and inner movements." },
    accent: "#75506f",
  },
  {
    id: "rhetoric_ideas", numeral: "IV", language: "multi",
    title: { fr: "Idées & rhétorique", en: "Ideas & rhetoric" },
    subtitle: { fr: "Penser & argumenter", en: "Thought & argument" },
    description: { fr: "Le vocabulaire de la pensée, de l’argument et des formes du discours.", en: "Vocabulary for thought, argument, and the forms of discourse." },
    accent: "#414f67",
  },
  {
    id: "character_society", numeral: "V", language: "multi",
    title: { fr: "Caractères & société", en: "Character & society" },
    subtitle: { fr: "Portraits humains", en: "Human portraits" },
    description: { fr: "Vertus, travers, attitudes et figures de la vie sociale.", en: "Virtues, failings, attitudes, and figures of social life." },
    accent: "#6e493b",
  },
  {
    id: "motion_manner", numeral: "VI", language: "multi",
    title: { fr: "Gestes & manières", en: "Gesture & manner" },
    subtitle: { fr: "Le mouvement précis", en: "Movement made precise" },
    description: { fr: "Des verbes et adverbes pour voir exactement comment les choses bougent.", en: "Verbs and adverbs that reveal exactly how things move." },
    accent: "#4b665d",
  },
  {
    id: "nature_colour", numeral: "VII", language: "multi",
    title: { fr: "Nature & sensation", en: "Nature & sensation" },
    subtitle: { fr: "Paysages sensibles", en: "Sensory landscapes" },
    description: { fr: "Arbres, reliefs, saisons et phénomènes ressentis.", en: "Trees, terrain, seasons, and felt phenomena." },
    accent: "#3f614c",
  },
  {
    id: "world_colour", numeral: "VIII", language: "multi",
    title: { fr: "Couleurs & matières", en: "Colour & material" },
    subtitle: { fr: "Le monde visible", en: "The visible world" },
    description: { fr: "Couleurs, objets, textures et détails du monde visible.", en: "Colours, objects, textures, and details of the visible world." },
    accent: "#8a593c",
  },
  {
    id: "classical_world", numeral: "IX", language: "multi",
    title: { fr: "Mondes anciens", en: "Ancient worlds" },
    subtitle: { fr: "Mythe & histoire", en: "Myth & history" },
    description: { fr: "Noms, récits et héritages de l’Antiquité gréco-romaine.", en: "Names, stories, and legacies from Greece and Rome." },
    accent: "#8a6a3e",
  },
  {
    id: "latin_heritage", numeral: "X", language: "multi",
    title: { fr: "Héritage latin", en: "Latin inheritance" },
    subtitle: { fr: "Racines & descendants", en: "Roots & descendants" },
    description: { fr: "Des mots modernes dont le latin éclaire encore le sens.", en: "Modern words whose meaning is illuminated by Latin roots." },
    accent: "#756044",
  },
];

const coreWords: Word[] = [
  {
    id: "liminal", listId: "nuances", term: "liminal", pronunciation: "/li.mi.nal/", level: 2,
    partOfSpeech: { fr: "adjectif", en: "adjective" },
    definition: { fr: "Qui se tient sur un seuil, dans un état de transition entre deux conditions.", en: "Occupying a threshold; poised in transition between two conditions." },
    etymology: { fr: "Du latin limen, « seuil ».", en: "From Latin limen, “threshold.”" },
    example: { fr: "L’aube est une heure liminale, ni tout à fait nuit ni déjà jour.", en: "Dawn is a liminal hour, neither wholly night nor yet day." },
    synonyms: { fr: ["transitoire", "intermédiaire"], en: ["transitional", "threshold"] },
    antonyms: { fr: ["stable", "établi"], en: ["settled", "established"] },
  },
  {
    id: "aporia", listId: "nuances", term: "aporie", termEn: "aporia", pronunciation: "/a.pɔ.ʁi/", level: 3,
    partOfSpeech: { fr: "nom féminin", en: "noun" },
    definition: { fr: "Difficulté logique insoluble; contradiction qui suspend le jugement.", en: "An irresolvable logical difficulty; a contradiction that suspends judgment." },
    etymology: { fr: "Du grec aporía, « absence de passage ».", en: "From Greek aporía, “without passage.”" },
    example: { fr: "Le dialogue s’achève sur une aporie plutôt que sur une certitude.", en: "The dialogue ends in aporia rather than certainty." },
    synonyms: { fr: ["impasse", "paradoxe"], en: ["impasse", "paradox"] },
    antonyms: { fr: ["solution", "certitude"], en: ["solution", "certainty"] },
  },
  {
    id: "perspicacious", listId: "nuances", term: "perspicace", termEn: "perspicacious", pronunciation: "/pɛʁ.spi.kas/", level: 2,
    partOfSpeech: { fr: "adjectif", en: "adjective" },
    definition: { fr: "Qui saisit avec finesse et rapidité ce qui échappe à l’évidence.", en: "Having a ready insight into things that elude the obvious." },
    etymology: { fr: "Du latin perspicax, « qui voit à travers ».", en: "From Latin perspicax, “seeing through.”" },
    example: { fr: "Sa lecture perspicace révéla le non-dit de la lettre.", en: "Her perspicacious reading revealed what the letter left unsaid." },
    synonyms: { fr: ["clairvoyant", "sagace"], en: ["insightful", "astute"] },
    antonyms: { fr: ["obtus", "inattentif"], en: ["obtuse", "unperceptive"] },
  },
  {
    id: "equivocal", listId: "nuances", term: "équivoque", termEn: "equivocal", pronunciation: "/e.ki.vɔk/", level: 1,
    partOfSpeech: { fr: "adjectif", en: "adjective" },
    definition: { fr: "Qui peut recevoir plusieurs interprétations et demeure volontairement incertain.", en: "Open to several interpretations and deliberately uncertain." },
    etymology: { fr: "Du latin aequivocus, « à voix égale ».", en: "From Latin aequivocus, “of equal voice.”" },
    example: { fr: "Son sourire équivoque ne confirmait ni ne démentait rien.", en: "His equivocal smile neither confirmed nor denied anything." },
    synonyms: { fr: ["ambigu", "incertain"], en: ["ambiguous", "uncertain"] },
    antonyms: { fr: ["univoque", "clair"], en: ["unequivocal", "clear"] },
  },
  {
    id: "ineffable", listId: "nuances", term: "ineffable", pronunciation: "/i.nɛ.fabl/", level: 2,
    partOfSpeech: { fr: "adjectif", en: "adjective" },
    definition: { fr: "Qu’aucune parole ne saurait exprimer avec justesse.", en: "Too great or subtle to be adequately expressed in words." },
    etymology: { fr: "Du latin ineffabilis, « qu’on ne peut dire ».", en: "From Latin ineffabilis, “that cannot be spoken.”" },
    example: { fr: "Une joie ineffable traversa son visage.", en: "An ineffable joy passed across her face." },
    synonyms: { fr: ["indicible", "inexprimable"], en: ["indescribable", "inexpressible"] },
    antonyms: { fr: ["exprimable", "ordinaire"], en: ["expressible", "ordinary"] },
  },
  {
    id: "petrichor", listId: "poetics", term: "pétrichor", termEn: "petrichor", pronunciation: "/pe.tʁi.kɔʁ/", level: 1,
    partOfSpeech: { fr: "nom masculin", en: "noun" },
    definition: { fr: "Odeur particulière que la pluie fait monter d’un sol longtemps sec.", en: "The distinctive scent released when rain falls on long-dry earth." },
    etymology: { fr: "Formé du grec petra, « pierre », et ichor, le sang des dieux.", en: "Coined from Greek petra, “stone,” and ichor, the blood of the gods." },
    example: { fr: "Après l’orage, le pétrichor emplissait le jardin.", en: "After the storm, petrichor filled the garden." },
    synonyms: { fr: ["odeur de pluie", "senteur de terre"], en: ["rain-scent", "earth scent"] },
  },
  {
    id: "susurrus", listId: "poetics", term: "susurrement", termEn: "susurrus", pronunciation: "/sy.syʁ.mɑ̃/", level: 2,
    partOfSpeech: { fr: "nom masculin", en: "noun" },
    definition: { fr: "Murmure très doux, semblable au frémissement des feuilles ou de l’eau.", en: "A whispering or rustling sound, like leaves or quiet water." },
    etymology: { fr: "Du latin susurrus, « murmure ».", en: "From Latin susurrus, “a whisper.”" },
    example: { fr: "Le susurrement des peupliers accompagnait leur lecture.", en: "The susurrus of the poplars accompanied their reading." },
    synonyms: { fr: ["murmure", "chuchotement"], en: ["whisper", "rustle"] },
    antonyms: { fr: ["fracas", "clameur"], en: ["clamour", "roar"] },
  },
  {
    id: "crepuscular", listId: "poetics", term: "crépusculaire", termEn: "crepuscular", pronunciation: "/kʁe.pys.ky.lɛʁ/", level: 1,
    partOfSpeech: { fr: "adjectif", en: "adjective" },
    definition: { fr: "Qui appartient à la lumière incertaine du soir ou du matin.", en: "Belonging to the uncertain light of dusk or dawn." },
    etymology: { fr: "Du latin crepusculum, « crépuscule ».", en: "From Latin crepusculum, “twilight.”" },
    example: { fr: "Une douceur crépusculaire enveloppait la place.", en: "A crepuscular softness settled over the square." },
    synonyms: { fr: ["vespéral", "tombant"], en: ["twilit", "dusky"] },
    antonyms: { fr: ["éclatant", "diurne"], en: ["brilliant", "noonday"] },
  },
  {
    id: "palimpsest", listId: "poetics", listIds: ["poetics", "french_literary", "rhetoric_ideas"], language: "fr", term: "palimpseste", termEn: "palimpsest", pronunciation: "/pa.lɛ̃.psɛst/", level: 3,
    partOfSpeech: { fr: "nom masculin", en: "noun" },
    definition: { fr: "Manuscrit effacé puis réécrit; par extension, œuvre où persistent plusieurs strates.", en: "A manuscript erased and reused; by extension, a work bearing multiple visible layers." },
    etymology: { fr: "Du grec palímpsêstos, « gratté de nouveau ».", en: "From Greek palímpsēstos, “scraped again.”" },
    example: { fr: "La ville se lisait comme un palimpseste de pierres et de souvenirs.", en: "The city read like a palimpsest of stone and memory." },
    synonyms: { fr: ["manuscrit réécrit", "texte stratifié"], en: ["reused parchment", "layered text"] },
    antonyms: { fr: ["texte intact"], en: ["pristine text"] },
  },
  {
    id: "lacuna", listId: "poetics", term: "lacune", termEn: "lacuna", pronunciation: "/la.kyn/", level: 1,
    partOfSpeech: { fr: "nom féminin", en: "noun" },
    definition: { fr: "Espace manquant dans un texte, une mémoire ou une connaissance.", en: "A missing space in a text, memory, or body of knowledge." },
    etymology: { fr: "Du latin lacuna, « creux, bassin ».", en: "From Latin lacuna, “hollow, pool.”" },
    example: { fr: "Cette page arrachée laisse une lacune dans le récit.", en: "The torn page leaves a lacuna in the narrative." },
    synonyms: { fr: ["manque", "hiatus"], en: ["gap", "hiatus"] },
    antonyms: { fr: ["continuité", "intégralité"], en: ["continuity", "entirety"] },
  },
  {
    id: "wherefore", listId: "inheritance", term: "wherefore", pronunciation: "/ˈwɛə.fɔː/", level: 2,
    partOfSpeech: { fr: "adverbe", en: "adverb" },
    definition: { fr: "Pour quelle raison; pourquoi — et non « où ».", en: "For what reason; why — rather than “where.”" },
    etymology: { fr: "De l’anglais moyen where + for.", en: "From Middle English where + for." },
    example: { fr: "« Wherefore art thou Romeo? » demande pourquoi il est Roméo.", en: "“Wherefore art thou Romeo?” asks why he is Romeo." },
    synonyms: { fr: ["pourquoi", "pour quelle raison"], en: ["why", "for what reason"] },
  },
  {
    id: "fain", listId: "inheritance", term: "fain", pronunciation: "/feɪn/", level: 2,
    partOfSpeech: { fr: "adverbe", en: "adverb" },
    definition: { fr: "Volontiers, avec plaisir; avec une inclination heureuse.", en: "Gladly or willingly; with pleased inclination." },
    etymology: { fr: "Du vieil anglais fægen, « joyeux ».", en: "From Old English fægen, “joyful.”" },
    example: { fr: "I would fain remain among these books.", en: "I would fain remain among these books." },
    synonyms: { fr: ["volontiers", "avec joie"], en: ["gladly", "willingly"] },
    antonyms: { fr: ["à regret", "malgré soi"], en: ["reluctantly", "unwillingly"] },
  },
  {
    id: "numinous", listId: "inheritance", term: "numineux", termEn: "numinous", pronunciation: "/ny.mi.nø/", level: 3,
    partOfSpeech: { fr: "adjectif", en: "adjective" },
    definition: { fr: "Qui inspire le sentiment mystérieux d’une présence sacrée.", en: "Evoking the mysterious feeling of a sacred presence." },
    etymology: { fr: "Du latin numen, « puissance divine ».", en: "From Latin numen, “divine power.”" },
    example: { fr: "Le silence de la nef avait quelque chose de numineux.", en: "The silence of the nave felt numinous." },
    synonyms: { fr: ["sacré", "mystérieux"], en: ["sacred", "mysterious"] },
    antonyms: { fr: ["profane", "prosaïque"], en: ["mundane", "prosaic"] },
  },
  {
    id: "antediluvian", listId: "inheritance", term: "antédiluvien", termEn: "antediluvian", pronunciation: "/ɑ̃.te.di.ly.vjɛ̃/", level: 2,
    partOfSpeech: { fr: "adjectif", en: "adjective" },
    definition: { fr: "Très ancien; littéralement, antérieur au Déluge biblique.", en: "Extremely old; literally, belonging to the time before the biblical Flood." },
    etymology: { fr: "Du latin ante, « avant », et diluvium, « déluge ».", en: "From Latin ante, “before,” and diluvium, “flood.”" },
    example: { fr: "La bibliothèque conservait un catalogue antédiluvien.", en: "The library preserved an antediluvian catalogue." },
    synonyms: { fr: ["archaïque", "ancestral"], en: ["archaic", "ancient"] },
    antonyms: { fr: ["moderne", "contemporain"], en: ["modern", "contemporary"] },
  },
  {
    id: "quixotic", listId: "inheritance", term: "quichottesque", termEn: "quixotic", pronunciation: "/ki.ʃɔ.tɛsk/", level: 2,
    partOfSpeech: { fr: "adjectif", en: "adjective" },
    definition: { fr: "D’un idéalisme généreux mais excessif, voué à se heurter au réel.", en: "Generously but impractically idealistic; destined to collide with reality." },
    etymology: { fr: "Du nom de Don Quichotte, héros de Cervantès.", en: "From Don Quixote, the hero of Cervantes." },
    example: { fr: "Son projet quichottesque séduisait précisément par son impossibilité.", en: "His quixotic project charmed precisely because it was impossible." },
    synonyms: { fr: ["idéaliste", "chimérique"], en: ["idealistic", "impractical"] },
    antonyms: { fr: ["pragmatique", "réaliste"], en: ["pragmatic", "realistic"] },
  },
];

export const words: Word[] = [...coreWords, ...catalogWords];
